import { useEffect } from "react";

type CapacitorModule = {
	Capacitor: {
		isNativePlatform: () => boolean;
	};
};

type AppModule = {
	App: {
		addListener: (
			event: string,
			cb: (state: { isActive: boolean }) => void,
		) => Promise<{ remove: () => void }>;
	};
};

type PreferencesModule = {
	Preferences: {
		set: (opts: { key: string; value: string }) => Promise<void>;
		get: (opts: { key: string }) => Promise<{ value: string | null }>;
		remove: (opts: { key: string }) => Promise<void>;
	};
};

export const SESSION_BACKUP_KEY = "apsu_session_active";

const NON_WORKSPACE_SEGMENTS = new Set([
	"",
	"sign-in",
	"sign-up",
	"workspaces",
	"admin",
	"invite",
]);

function getWorkspaceSlug(): string | undefined {
	const firstSegment = window.location.pathname.split("/")[1] ?? "";
	if (NON_WORKSPACE_SEGMENTS.has(firstSegment)) return undefined;
	return firstSegment || undefined;
}

export function useNativeSession(): void {
	useEffect(() => {
		let cleanup: (() => void) | undefined;

		(async () => {
			try {
				const coreMod = "@capacitor/core";
				const appMod = "@capacitor/app";
				const prefsMod = "@capacitor/preferences";
				const { Capacitor } = (await import(
					/* @vite-ignore */ coreMod
				)) as CapacitorModule;
				if (!Capacitor.isNativePlatform()) return;

				const { App } = (await import(/* @vite-ignore */ appMod)) as AppModule;
				const { Preferences } = (await import(
					/* @vite-ignore */ prefsMod
				)) as PreferencesModule;

				// Record that a session is active on initial load
				const slug = getWorkspaceSlug();
				if (slug) {
					await Preferences.set({
						key: SESSION_BACKUP_KEY,
						value: JSON.stringify({ slug, ts: Date.now() }),
					});
				}

				const listener = await App.addListener(
					"appStateChange",
					async (state: { isActive: boolean }) => {
						if (!state.isActive) return;

						// App resumed — check if session is still valid
						const workspaceSlug = getWorkspaceSlug();
						if (!workspaceSlug) {
							// On a non-workspace page (sign-in, etc.) — nothing to refresh
							return;
						}

						try {
							const res = await fetch(`/api/tenant/${workspaceSlug}/me`, {
								credentials: "include",
							});

							if (res.ok) {
								// Session valid — update the backup timestamp
								await Preferences.set({
									key: SESSION_BACKUP_KEY,
									value: JSON.stringify({
										slug: workspaceSlug,
										ts: Date.now(),
									}),
								});
							} else {
								// Session expired or invalid — check if we had a prior session
								const backup = await Preferences.get({
									key: SESSION_BACKUP_KEY,
								});

								if (backup.value) {
									// Had an active session before — redirect to sign-in
									await Preferences.remove({
										key: SESSION_BACKUP_KEY,
									});
									window.location.href = "/sign-in";
								}
							}
						} catch {
							// Network error on resume — silently ignore, will retry next resume
						}
					},
				);

				cleanup = () => {
					listener.remove();
				};
			} catch {
				// Not in a Capacitor environment — no-op in browsers
			}
		})();

		return () => {
			cleanup?.();
		};
	}, []);
}
